/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/shared/lib/utils.ts
 * layer: web
 * area: lib
 * kind: lib
 * web_root: lib
 * relpath: web/src/platform/shared/lib/utils.ts
 * filename: utils.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

import { clsx, type ClassValue } from 'clsx';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import { getDisplayFormats, getDisplayTimeZone } from '@/platform/shared/lib/timezone';

/**
 * Combines clsx and tailwind-merge for conditional class names
 * that properly handle Tailwind CSS class conflicts.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a number as currency (USD)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Formats a number with thousands separators
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/** Parse a date string or Date into a Date object. Returns null for falsy/invalid input. */
function toDate(dateString: string | number | Date | null | undefined): Date | null {
  if (!dateString) return null;
  if (dateString instanceof Date) return isNaN(dateString.getTime()) ? null : dateString;
  if (typeof dateString === 'number') {
    const n = new Date(dateString);
    return isNaN(n.getTime()) ? null : n;
  }
  try {
    const d = parseISO(dateString);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/**
 * A calendar date with no time ('YYYY-MM-DD', e.g. a due date or holiday).
 * parseISO reads it as browser-local midnight; converting that instant to the
 * display timezone moves it to the previous day for any viewer east of the
 * workspace zone. Calendar dates are therefore formatted as-is, in UTC, with
 * no zone conversion.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function calendarDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const match = DATE_ONLY.exec(value.trim());
  if (!match) return null;
  const d = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formats a date as a relative time string (e.g., "2 hours ago", "3 days ago").
 * Returns '—' for null/undefined/invalid dates.
 */
export function formatRelativeTime(dateString: string | number | Date | null | undefined): string {
  const d = toDate(dateString);
  if (!d) return '—';
  return formatDistanceToNow(d, { addSuffix: true });
}

/** Date parts of `d` in the display timezone (Australia/Melbourne by default). */
function displayParts(
  d: Date,
  withTime: boolean,
  hour12: boolean,
  timeZone: string = getDisplayTimeZone()
): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit', hour12 } : {}),
  }).formatToParts(d);
  const out: Record<string, string> = {};
  for (const p of parts) if (p.type !== 'literal') out[p.type] = p.value;
  return out;
}

/** Numeric year/month/day of `d` in the display timezone. */
function numericDateParts(
  d: Date,
  timeZone: string = getDisplayTimeZone()
): { y: string; m: string; day: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const out: Record<string, string> = {};
  for (const p of parts) if (p.type !== 'literal') out[p.type] = p.value;
  return { y: out.year, m: out.month, day: out.day };
}

/** The date half, honouring the workspace date_format once one is saved. */
function datePart(d: Date, timeZone?: string): string {
  const { dateFormat } = getDisplayFormats();
  if (dateFormat) {
    const { y, m, day } = numericDateParts(d, timeZone);
    if (dateFormat === 'DD/MM/YYYY') return `${day}/${m}/${y}`;
    if (dateFormat === 'MM/DD/YYYY') return `${m}/${day}/${y}`;
    return `${y}-${m}-${day}`;
  }
  const p = displayParts(d, false, true, timeZone);
  return `${p.month} ${p.day}, ${p.year}`;
}

/** The time half ("2:30 PM" or "14:30"), honouring the workspace time_format. */
function timePart(d: Date): string {
  const hour12 = getDisplayFormats().timeFormat !== '24h';
  const p = displayParts(d, true, hour12);
  if (!hour12) return `${p.hour.padStart(2, '0').replace(/^24/, '00')}:${p.minute}`;
  return `${p.hour}:${p.minute} ${(p.dayPeriod ?? '').toUpperCase()}`.trim();
}

/**
 * Formats a date (e.g., "Jan 15, 2026", or the workspace date_format such as
 * "15/01/2026") in the display timezone, which is the workspace timezone once
 * known and Australia/Melbourne otherwise — never the viewer's browser zone.
 * Returns '—' for null/undefined/invalid dates.
 */
export function formatDate(dateString: string | number | Date | null | undefined): string {
  const calendar = calendarDate(dateString);
  if (calendar) return datePart(calendar, 'UTC');
  const d = toDate(dateString);
  if (!d) return '—';
  return datePart(d);
}

/**
 * Formats a date and time (e.g., "Jan 15, 2026 2:30 PM") in the display
 * timezone, honouring the workspace date/time formats. Returns '—' for
 * null/undefined/invalid dates.
 */
export function formatDateTime(dateString: string | number | Date | null | undefined): string {
  // A calendar date has no time of day to show.
  const calendar = calendarDate(dateString);
  if (calendar) return datePart(calendar, 'UTC');
  const d = toDate(dateString);
  if (!d) return '—';
  return `${datePart(d)} ${timePart(d)}`;
}

/**
 * Formats a time of day (e.g., "2:30 PM" or "14:30") in the display timezone.
 * Returns '—' for null/undefined/invalid dates.
 */
export function formatTime(dateString: string | number | Date | null | undefined): string {
  const d = toDate(dateString);
  if (!d) return '—';
  return timePart(d);
}

/**
 * Truncates a string to a maximum length with ellipsis
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}

/**
 * Capitalizes the first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Converts a snake_case or kebab-case string to Title Case
 */
export function toTitleCase(str: string): string {
  return str
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Generates a random UUID (for client-side IDs)
 */
export function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Debounces a function call
 */
export function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Checks if we're running on Mac
 */
export function isMac(): boolean {
  return typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
}

/**
 * Returns the appropriate modifier key label (Cmd on Mac, Ctrl elsewhere)
 */
export function getModifierKey(): string {
  return isMac() ? 'Cmd' : 'Ctrl';
}
